import PublicLayout from "@/components/Common/Containers/PublicLayout";
import { customToast } from "@/components/Common/Toast";
import { CreateNewPasswordForm } from "@WealthierAI/core";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createPasswordInput,
  setNewPasswordAPI,
  verifyVerificationCode,
} from "../../../services/user";
import { Benefits, ErrorState, LoadingState } from "./ForgotPasswordComponents";

const VERIFICATION_STATUS = {
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
} as const;

type VerificationStatus =
  (typeof VERIFICATION_STATUS)[keyof typeof VERIFICATION_STATUS];

const VerifyResetPassword: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationStatus, setVerificationStatus] =
    useState<VerificationStatus>(VERIFICATION_STATUS.LOADING);
  const [userAccountID, setUserAccountID] = useState<string | null>(null);

  const handleCodeVerification = useCallback(async (resetCode: string) => {
    try {
      const response = await verifyVerificationCode(resetCode);

      if (response.status !== 200) {
        throw new Error("Verification failed");
      }

      if (response.data === "Record Not found into database") {
        setVerificationStatus(VERIFICATION_STATUS.ERROR);
        return;
      }

      const userID = response.data?.item2?.userAccountID;
      if (!userID) {
        throw new Error("Invalid response format");
      }

      setVerificationStatus(VERIFICATION_STATUS.SUCCESS);
      setUserAccountID(userID.toString());
    } catch {
      setVerificationStatus(VERIFICATION_STATUS.ERROR);
    }
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const resetCode = searchParams.get("PasswordResetVerificationCode");

    if (resetCode) {
      handleCodeVerification(resetCode);
    } else {
      setVerificationStatus(VERIFICATION_STATUS.ERROR);
    }
  }, [location.search, handleCodeVerification]);

  const handlePasswordReset = async (data: createPasswordInput) => {
    const { password } = data;

    if (!userAccountID) {
      //toast.error("User ID not found");
      customToast(`User ID not found`, "error");
      return;
    }

    try {
      setLoading(true);
      const searchParams = new URLSearchParams(location.search);
      const resetCode = searchParams.get("PasswordResetVerificationCode");
      if (!resetCode) {
        customToast(`Reset code not found`, "error");
        setLoading(false);
        return;
      }
      await setNewPasswordAPI(password, userAccountID, resetCode);
      //toast.success("Password successfully reset.");
      customToast(`Password successfully reset`, "success");
      navigate("/");
    } catch {
      //toast.error("Failed to reset password. Please try again.");
      customToast(`Failed to reset password. Please try again.`, "error");
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    switch (verificationStatus) {
      case VERIFICATION_STATUS.LOADING:
        return <LoadingState />;
      case VERIFICATION_STATUS.ERROR:
        return <ErrorState onRetry={() => navigate("/forgot-password")} />;
      case VERIFICATION_STATUS.SUCCESS:
        return (
          <div className="font-outfit w-full md:w-96 md:mt-10">
            <div className="md:shadow-lg md:bg-white rounded-3xl md:p-8">
              <h1 className="text-3xl font-semibold">Reset password</h1>
              <p className="py-4 text-lg">
                Reset your password to ensure your account stays safe.
              </p>

              <CreateNewPasswordForm
                btnText="Reset Password"
                onSubmit={handlePasswordReset}
                loading={loading}
                error={error}
                resetError={() => setError(null)}
              />
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <PublicLayout>
        <div className="flex-grow flex items-center justify-center px-4 md:bg-gradient-to-b from-custom-blue to-custom-purple h-full">
          <div className="w-full max-w-4xl flex flex-col md:flex-row">
            <Benefits />
            {renderContent()}
          </div>
        </div>
      </PublicLayout>
    </>
  );
};

export default VerifyResetPassword;
